<section class="todo-screen" aria-label="My ToDo screen">
  <div class="todo-screen__panel">
    <header class="todo-screen__header">
      <div>
        <p class="todo-screen__eyebrow">Personal planning</p>
        <h2>My ToDo</h2>
        <p class="todo-screen__subtitle">Keep a lightweight checklist for yourself alongside the rest of your work.</p>
      </div>

      <div class="todo-screen__stats" aria-label="Todo summary">
        <article class="todo-screen__stat">
          <span>Total</span>
          <strong>{{ summary().total }}</strong>
        </article>
        <article class="todo-screen__stat">
          <span>Open</span>
          <strong>{{ summary().pending }}</strong>
        </article>
        <article class="todo-screen__stat">
          <span>Done</span>
          <strong>{{ summary().completed }}</strong>
        </article>
      </div>
    </header>

    <section class="todo-screen__composer">
      <mat-form-field appearance="outline" class="todo-screen__field">
        <mat-label>Add a to-do</mat-label>
        <input
          matInput
          [value]="draft()"
          (input)="draft.set($any($event.target).value)"
          (keydown.enter)="onDraftEnter($event)"
          placeholder="Write a quick reminder or checklist item"
        />
      </mat-form-field>
      <button type="button" mat-flat-button color="primary" [disabled]="!canAddTodo()" (click)="addTodo()">Add</button>
    </section>

    @if (loading()) {
      <mat-card class="todo-screen__empty">
        <mat-icon aria-hidden="true">hourglass_top</mat-icon>
        <h3>Loading todos</h3>
        <p>Fetching your saved checklist items.</p>
      </mat-card>
    } @else if (!summary().total) {
      <mat-card class="todo-screen__empty">
        <mat-icon aria-hidden="true">checklist</mat-icon>
        <h3>No to-dos yet</h3>
        <p>Add your first todo to start tracking quick personal work items.</p>
      </mat-card>
    } @else {
      <div class="todo-screen__board">
        <section class="todo-screen__column" aria-label="Open todos">
          <div class="todo-screen__column-head">
            <h3>Open</h3>
            <span>{{ pendingTodos().length }}</span>
          </div>

          <div class="todo-screen__list" cdkDropList [cdkDropListData]="pendingTodos()" (cdkDropListDropped)="dropTodo(pendingTodos(), $event)">
            @for (todo of pendingTodos(); track trackTodo($index, todo)) {
              <mat-card class="todo-card" cdkDrag [cdkDragDisabled]="isEditing(todo.id) || saving()">
                <button type="button" class="todo-card__drag-handle" cdkDragHandle aria-label="Drag to reorder todo">
                  <mat-icon aria-hidden="true">drag_indicator</mat-icon>
                </button>
                @if (isEditing(todo.id)) {
                  <div class="todo-card__editor">
                    <mat-form-field appearance="outline" class="todo-card__editor-field">
                      <mat-label>Edit to-do</mat-label>
                      <input
                        matInput
                        [value]="editingDraft()"
                        (input)="editingDraft.set($any($event.target).value)"
                        (keydown)="onEditKeydown($event, todo)"
                        placeholder="Update this to-do"
                        cdkFocusInitial
                      />
                    </mat-form-field>
                  </div>
                } @else {
                  <mat-checkbox [checked]="todo.is_completed" (change)="toggleTodo(todo, $event)">
                    <span class="todo-card__title">{{ todo.title }}</span>
                  </mat-checkbox>
                }
                <div class="todo-card__actions">
                  @if (isEditing(todo.id)) {
                    <button type="button" mat-icon-button aria-label="Save todo" [disabled]="saving() || !editingDraft().trim()" (click)="saveEditing(todo)">
                      <mat-icon aria-hidden="true">check</mat-icon>
                    </button>
                    <button type="button" mat-icon-button aria-label="Cancel editing todo" [disabled]="saving()" (click)="cancelEditing()">
                      <mat-icon aria-hidden="true">close</mat-icon>
                    </button>
                  } @else {
                    <button type="button" mat-icon-button aria-label="Edit todo" [disabled]="saving()" (click)="startEditing(todo)">
                      <mat-icon aria-hidden="true">edit</mat-icon>
                    </button>
                    <button type="button" mat-icon-button aria-label="Delete todo" (click)="deleteTodo(todo)">
                      <mat-icon aria-hidden="true">delete</mat-icon>
                    </button>
                  }
                </div>
              </mat-card>
            } @empty {
              <div class="todo-screen__placeholder">Everything here is complete.</div>
            }
          </div>
        </section>

        <section class="todo-screen__column todo-screen__column--completed" aria-label="Completed todos">
          <div class="todo-screen__column-head">
            <h3>Completed</h3>
            <span>{{ completedTodos().length }}</span>
          </div>

          <div class="todo-screen__list" cdkDropList [cdkDropListData]="completedTodos()" (cdkDropListDropped)="dropTodo(completedTodos(), $event)">
            @for (todo of completedTodos(); track trackTodo($index, todo)) {
              <mat-card class="todo-card todo-card--completed" cdkDrag [cdkDragDisabled]="isEditing(todo.id) || saving()">
                <button type="button" class="todo-card__drag-handle" cdkDragHandle aria-label="Drag to reorder todo">
                  <mat-icon aria-hidden="true">drag_indicator</mat-icon>
                </button>
                @if (isEditing(todo.id)) {
                  <div class="todo-card__editor">
                    <mat-form-field appearance="outline" class="todo-card__editor-field">
                      <mat-label>Edit to-do</mat-label>
                      <input
                        matInput
                        [value]="editingDraft()"
                        (input)="editingDraft.set($any($event.target).value)"
                        (keydown)="onEditKeydown($event, todo)"
                        placeholder="Update this to-do"
                        cdkFocusInitial
                      />
                    </mat-form-field>
                  </div>
                } @else {
                  <mat-checkbox [checked]="todo.is_completed" (change)="toggleTodo(todo, $event)">
                    <span class="todo-card__title">{{ todo.title }}</span>
                  </mat-checkbox>
                }
                <div class="todo-card__actions">
                  @if (isEditing(todo.id)) {
                    <button type="button" mat-icon-button aria-label="Save todo" [disabled]="saving() || !editingDraft().trim()" (click)="saveEditing(todo)">
                      <mat-icon aria-hidden="true">check</mat-icon>
                    </button>
                    <button type="button" mat-icon-button aria-label="Cancel editing todo" [disabled]="saving()" (click)="cancelEditing()">
                      <mat-icon aria-hidden="true">close</mat-icon>
                    </button>
                  } @else {
                    <button type="button" mat-icon-button aria-label="Edit todo" [disabled]="saving()" (click)="startEditing(todo)">
                      <mat-icon aria-hidden="true">edit</mat-icon>
                    </button>
                    <button type="button" mat-icon-button aria-label="Delete todo" (click)="deleteTodo(todo)">
                      <mat-icon aria-hidden="true">delete</mat-icon>
                    </button>
                  }
                </div>
              </mat-card>
            } @empty {
              <div class="todo-screen__placeholder">Completed items will collect here.</div>
            }
          </div>
        </section>
      </div>
    }
  </div>
</section>
